import "./Experience.css";
import { images } from "../assets/images";
import { motion, useScroll, useTransform, useSpring, useMotionValue, useMotionValueEvent } from "framer-motion";
import { FaExternalLinkAlt, FaPlane, FaLock } from "react-icons/fa";
import { useRef, useEffect } from "react";
import { HiddenDevComment } from "./DeveloperMode";
import { devComments } from "../config/devComments";
import { useDebug } from "../contexts/DebugContext";
import { getAnimProps } from "../utils/anim";
import { LINKS } from "../config/links";

// Represents an external link within a highlight paragraph (e.g., "View on Play Store")
type HighlightLink = { label: string; url: string; locked?: boolean };
// A highlight item that contains both text and a set of associated external links
type HighlightWithLinks = { text: React.ReactNode; links: HighlightLink[] };
// Company IP (internal project) with name and tech stack description
type CompanyIP = { name: string; stack: string };

// Full schema for a single experience entry on the timeline
type ExperienceItem = {
  company: string;
  companyLink: string;
  role: string;
  period: string;
  image?: string;
  logoWidth?: number;
  logoHeight?: number;
  companyIP?: CompanyIP[];
  subLinks?: { label: string; url: string }[];
  highlights: (React.ReactNode | HighlightWithLinks)[];
};

// Renders a company IP item with a lock icon, indicating it's internal/proprietary with no public link
const CompanyIPItem = ({ name, stack }: { name: string; stack: string }) => (
  <div className="ip-item">
    <div className="ip-item-main">
      <span className="ip-item-name">{name}</span>
      <span className="ip-item-stack">{stack}</span>
    </div>
    <span className="ip-item-lock" data-tooltip="Company IP - no public link" aria-label="Company IP">
      <FaLock />
    </span>
  </div>
);

const DesktopScrollPlane = ({
  containerRef,
  timelineRef,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  timelineRef: React.RefObject<HTMLDivElement | null>;
}) => {
  // CRITICAL: framer-motion v12's useScroll() uses IntersectionObserver internally, which fires
  // DISCRETE callbacks, not continuous per-pixel updates. The progress value changes in steps, not
  // smoothly. That is WHY useSpring is applied below: it interpolates between discrete values to
  // create smooth continuous motion. Without useSpring, the plane would jump between positions.
  // Scroll offset ["start center", "end 90%"]: animation starts when the container's top reaches
  // the viewport center, and ends when the container's bottom reaches 90% from the viewport top.
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end 90%"],
  });

  // Spring values: high damping (45) prevents overshoot/bounce. Mass (1.5) adds inertia so the
  // plane doesn't stop instantly. restDelta: 0.005 ensures the animation stops precisely (avoids
  // micro-oscillations).
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 150,
    damping: 45,
    mass: 1.5,
    restDelta: 0.005,
  });

  // planeEndRef stores the measured end position of the plane animation. Measured from the last
  // timeline item's offsetTop. The 15px subtraction and 40px minimum ensure the plane stops within
  // the timeline, not beyond it.
  const planeEndRef = useRef(0);

  // Measures the timeline height on mount/resize so the plane animation knows how far to travel.
  // The setTimeout(updateMaxY, 100) is a WORKAROUND for a race condition: the DOM may not have
  // fully rendered by the time useEffect fires. 100ms gives React time to complete layout.
  // The resize listener ensures the plane endpoint recalculates on window resize.
  useEffect(() => {
    const updateMaxY = () => {
      if (timelineRef.current) {
        const items = timelineRef.current.querySelectorAll(".timeline-item");
        if (items.length > 0) {
          const lastItem = items[items.length - 1] as HTMLElement;
          const val = Math.max(lastItem.offsetTop - 15, 40);
          planeEndRef.current = val;
        }
      }
    };
    const timeoutId = setTimeout(updateMaxY, 100);
    window.addEventListener("resize", updateMaxY);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateMaxY);
    };
  }, [timelineRef]);

  // Transforms scroll progress into a Y-offset for the plane icon, mapping 0-1 to the usable range.
  // Formula: 40 + pos * (end - 40). 40 is the starting Y position (top of timeline), end - 40 is
  // the full travel distance. pos ranges from 0 to 1.
  // If end <= 40 (timeline is empty or not measured), returns 0 (plane stays hidden).
  const planeY = useTransform(smoothProgress, (pos: number) => {
    const end = planeEndRef.current;
    if (end <= 40) return 0;
    return 40 + pos * (end - 40);
  });

  // Motion value for plane rotation (90 = flying down, -90 = flying up)
  const planeRotate = useMotionValue(90);

  // Detects scroll direction and rotates the plane icon accordingly.
  // useMotionValueEvent with scrollYProgress.getPrevious() is a framer-motion API specifically for
  // detecting direction changes. Comparing current vs previous progress tells us if the user is
  // scrolling down (90 degree plane tilt) or up (-90 degree tilt). This is needed because useSpring
  // smooths the value, making direction detection impossible from the smoothed value alone.
  useMotionValueEvent(scrollYProgress, "change", (current) => {
    const previous = scrollYProgress.getPrevious();

    if (previous !== undefined) {
      if (current > previous) {
        planeRotate.set(90);
      } else if (current < previous) {
        planeRotate.set(-90);
      }
    }
  });

  return (
    <motion.div
      className="scroll-plane"
      style={{
        y: planeY,
        rotate: planeRotate,
      }}
    >
      <FaPlane />
    </motion.div>
  );
};

const Experience = () => {
  const { config: debug } = useDebug();
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Work experience entries in chronological order; each includes company info, role, period, logo, and highlights with optional external links
  const experiences: ExperienceItem[] = [
    {
      company: "Extramarks",
      companyLink: LINKS.EXTRAMARKS,
      role: "Senior Software Developer",
      period: "Jul 2025 - Dec 2025",
      image: images.extramarksLogo,
      logoWidth: 250,
      logoHeight: 122,
      // Solo senior developer at an Ed-Tech company building gamified learning tools, 3D games, and internal web modules
      highlights: [
        <span key="1">
          Joined Extramarks to build <strong>interactive learning tools</strong> for the Ed-Tech space.
        </span>,
        <span key="2">
          Acted as a <strong>Solo Senior Developer</strong> taking full ownership of educational games and internal web modules.
        </span>,
        {
          text: (
            <span>
              Architected a <strong>3D Bird Sanctuary</strong> exploration game teaching biodiversity through play. Built in Unity with
              custom shaders, complex state machines, and an asset-bundle streaming pipeline - optimizing the WebGL build from{" "}
              <strong>327MB down to just 32MB (90% reduction)</strong> while retaining full visual fidelity.
            </span>
          ),
          links: [{ label: "Bird Sanctuary 3D", url: "#", locked: true }],
        },
        {
          text: (
            <span>
              Designed and shipped a <strong>2D spatial visualization</strong> pattern game where students learn color and shape recognition
              through interactive mix-and-match gameplay. Built a <strong>level generation tool</strong> using permutation-based scripting
              across shapes and colors, automatically creating <strong>600+ levels</strong> for adaptive difficulty progression.
            </span>
          ),
          links: [{ label: "2D Spatial Visualization", url: "#", locked: true }],
        },
        {
          text: (
            <span>
              Built <em>React JS</em>, <em>HTML</em> and <em>CSS</em> modules for internal application interfaces used by content teams.
            </span>
          ),
          links: [],
        },
        {
          text: (
            <span>
              Created <em>WebGL</em> exports and custom <em>Windows tools</em> for teachers to dynamically generate question sets.
            </span>
          ),
          links: [],
        },
      ],
    },
    {
      company: "MadOverGames",
      companyLink: LINKS.MAD_OVER_GAMES,
      role: "Senior Game Developer",
      period: "Jul 2021 - Jun 2025",
      image: images.madOverGamesLogo,
      logoWidth: 174,
      logoHeight: 170,
      // 4 years at a mobile gaming company; contributed to 25M+ download titles with PvP systems, bot AI, clan features, and portfolio optimization
      // companyIP: [
      //   { name: 'PvP Match',              stack: 'Unity · C# · WebSocket · Firebase · MMR' },
      //   { name: 'Bubble Shooter Space',   stack: 'Unity · C# · Analytics · Monetisation' },
      //   { name: 'Bubble Shooter Classic', stack: 'Unity · C# · 25M+ downloads' },
      //   { name: 'Bubble Shooter Pro',     stack: 'Unity · C# · Clan systems · 99.85% crash-free' },
      //   { name: 'Kitten Games',           stack: 'Unity · C# · Casual · iOS' },
      //   { name: 'Gummy Land',             stack: 'Unity · C# · Casual' },
      // ],
      subLinks: [
        { label: "MOG Android", url: LINKS.MOG_ANDROID_DEV_PAGE },
        { label: "MOG iOS", url: LINKS.MOG_IOS_DEV_PAGE },
      ],
      highlights: [
        <span key="0">
          Started as <strong>Game Developer</strong>, promoted to <strong>Senior Game Developer</strong> within ~3 years for consistent
          impact on top grossing titles.
        </span>,
        <span key="1">
          Led advanced game development focusing on <strong>multiplayer systems</strong> and <strong>Bot driven gameplay</strong>.
        </span>,
        {
          text: (
            <span>
              Architected PvP multiplayer system with real-time matchmaking and <strong>MMR based algorithms</strong>.
            </span>
          ),
          links: [{ label: "PvP Match", url: LINKS.PVP_MATCH }],
        },
        <span key="3">
          Built a bot that completes levels in <strong>7 seconds on average</strong> in fast play mode using pathfinding,{" "}
          <em>Ghost Bubble</em> targeting, and probability-based decisions.
        </span>,
        <span key="4">
          Integrated <em>WebSocket</em> communication and <em>Firebase</em> real-time databases for seamless multiplayer experiences.
        </span>,
        {
          text: (
            <span>
              Spearheaded full-stack development of <strong>Bubble Shooter Space</strong>, reaching <strong>200k+ users</strong> with{" "}
              <strong>4.8</strong> App Store ratings.
            </span>
          ),
          links: [{ label: "Space Game", url: LINKS.BUBBLE_SHOOTER_SPACE_STORE }],
        },
        {
          text: (
            <span>
              Contributed to <strong>Bubble Shooter Classic (25M+ Downloads)</strong> by implementing automated testing.
            </span>
          ),
          links: [{ label: "Classic", url: LINKS.BUBBLE_SHOOTER_CLASSIC }],
        },
        {
          text: (
            <span>
              Led clan feature development in <strong>Bubble Shooter Pro</strong>, boosting 30-day active users from{" "}
              <strong>134k to 170k</strong> and achieving a <strong>99.85% crash-free</strong> session rate.
            </span>
          ),
          links: [{ label: "Pro", url: LINKS.BUBBLE_SHOOTER_PRO }],
        },
        <span key="pooling">
          Implemented an <strong>object pooling system</strong> across all titles using reusable preallocated pools to prevent excessive
          instantiation and memory leaks.
        </span>,
        <span key="legacy-reduce">
          Further optimized a <strong>100MB game</strong> that had been live for years and was already well-optimized, reducing its
          footprint by <strong>10MB</strong> through systematic asset cleanup, sprite/audio compression, and dependency removal, all while
          maintaining <strong>existing asset quality standards</strong>.
        </span>,
        {
          text: (
            <span>
              Managed diverse game portfolio including <strong>Kitten Games</strong> and <strong>Gummy Land</strong>.
            </span>
          ),
          links: [
            { label: "Kitten Games", url: LINKS.KITTEN_GAMES },
            { label: "Gummy Land", url: LINKS.GUMMY_LAND },
          ],
        },
        <span key="5">
          Created engaging <strong>Playable Ads</strong> using <em>HTML5</em>, <em>CSS3</em>, <em>JavaScript</em>, and the <em>Phaser</em>{" "}
          framework.
        </span>,
        <span key="6">
          Developed cross-platform <strong>Facebook Instant Games</strong> with <em>JavaScript</em> data persistence.
        </span>,
      ],
    },
    {
      company: "ThinkNext Technologies",
      companyLink: LINKS.THINK_NEXT,
      role: "Android Developer Intern",
      period: "Jan 2021 - May 2021",
      image: images.thinkNextLogo,
      logoWidth: 400,
      logoHeight: 136,
      // Entry-level Android development internship building casual games and apps
      highlights: [
        <span key="1">
          Developed Android applications including a <strong>Brain Trainer</strong> math quiz, a music app, and a Flappy Bird clone
          utilizing <em>Java</em> and <em>Libgdx</em>.
        </span>,
      ],
    },
  ];

  return (
    <section id="experience" className="experience">
      <div className="container">
        <motion.h2 {...getAnimProps("EXPERIENCE", debug)}>
          Work Experience
          {/* Dev comment - plane animation */}
          <HiddenDevComment {...devComments.experience} />
        </motion.h2>

        {/* Timeline with scroll-driven paper plane animation and staggered item entrance */}
        <div className="timeline-container" ref={containerRef}>
          {/* Animated plane icon. Y position follows scroll progress via useTransform; rotation follows
              scroll direction via useMotionValue. These are animated independently because Y uses the
              smoothed (useSpring) value while rotation uses the raw direction signal. */}
          <DesktopScrollPlane containerRef={containerRef} timelineRef={timelineRef} />
          <div className="timeline" ref={timelineRef}>
            {experiences.map((exp, index) => (
              /* Each timeline item fades/slides in with a staggered delay when scrolled into view */
              <motion.div
                key={exp.company}
                className="timeline-item"
                {...(debug.ANIMATIONS && debug.ANIM_SECTIONS?.EXPERIENCE ? getAnimProps("EXPERIENCE", debug, { delay: index * 0.1 }) : {})}
              >
                <div className="timeline-dot"></div>
                <div className="timeline-content glass-panel">
                  {/* Role, period, and company logo with image error fallback */}
                  <div className="timeline-header">
                    <div className="header-text">
                      <h3>{exp.role}</h3>
                      {/* Company name with external link and optional sub-links (e.g., Play Store, App Store) */}
                      <h4 className="timeline-company">
                        <a href={exp.companyLink} target="_blank" rel="noopener noreferrer">
                          {exp.company} <FaExternalLinkAlt size={12} />
                        </a>
                        <span className="timeline-period">{exp.period}</span>
                        {exp.subLinks && (
                          <span className="company-sublinks-inline">
                            {exp.subLinks.map((link) => (
                              <a
                                key={link.url}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="company-sublink-inline"
                              >
                                {link.label} <FaExternalLinkAlt size={8} />
                              </a>
                            ))}
                          </span>
                        )}
                      </h4>
                    </div>
                    {exp.image && (
                      <a
                        href={exp.companyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`timeline-image logo-${exp.company.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                      >
                        <img
                          src={exp.image}
                          alt={exp.company}
                          draggable="false"
                          loading="lazy"
                          decoding="async"
                          className="company-logo"
                          width={exp.logoWidth}
                          height={exp.logoHeight} /* onError={handleLogoError} */
                        />
                      </a>
                    )}
                  </div>
                  {/* Company IP projects - rendered as locked items (internal, no public link) */}
                  {exp.companyIP && exp.companyIP.length > 0 && (
                    <div className="company-ip">
                      {exp.companyIP.map((proj) => (
                        <CompanyIPItem key={proj.name} name={proj.name} stack={proj.stack} />
                      ))}
                    </div>
                  )}
                  {/* Bulleted highlights with inline external links (unlocked link or locked badge) */}
                  <ul className="timeline-highlights">
                    {exp.highlights.map((item, hIndex) => (
                      <li key={"highlight-" + hIndex}>
                        {"text" in (item as HighlightWithLinks) ? (
                          <>
                            {(item as HighlightWithLinks).text}
                            <div className="highlight-links">
                              {(item as HighlightWithLinks).links.map((link: HighlightLink) => {
                                if (link.locked) {
                                  return (
                                    <span
                                      key={link.label}
                                      className="highlight-link highlight-link--locked"
                                      data-tooltip="Internal / Company IP - no public link"
                                    >
                                      <FaLock size={10} /> {link.label}
                                    </span>
                                  );
                                }
                                return (
                                  <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer" className="highlight-link">
                                    {link.label} <FaExternalLinkAlt size={10} />
                                  </a>
                                );
                              })}
                            </div>
                          </>
                        ) : (
                          <>{item as React.ReactNode}</>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
